















































import { Component, Vue, Prop } from "vue-property-decorator";
import DetailHeader from "../components/DetailHeader.vue";
import ChecklistQuestion from "../components/ChecklistQuestion.vue";
import store from "@/store/store";
import { loadExamDetail, loadJobDetail } from "@/common/jobLoader";
import { Question, Answer } from "../common/interfaces";
import axios from "axios";
import QuotedParts from "@/components/requiredParts/QuotedParts.vue";
import JobDetail from "@/views/JobDetail.vue";
import { ActionNames } from "@/store/actions";
import { BigQuestion } from "./ExamDetail.vue";
import LongQuestion from "@/components/LongQuestion.vue";
import DateTimePicker from "@/components/DateTimePicker.vue";
import moment from "moment";

@Component({
  components: {
    DetailHeader,
    ChecklistQuestion,
    LongQuestion,
    DateTimePicker,
  },
})
export default class Checklist extends Vue {
  protected loadingJob: boolean = true;
  protected loadingQuestion: boolean = true;
  protected loadingAnswer: boolean = true;
  protected loadingPage: boolean = true;

  protected questionList: { [k: string]: Question[] } = {};
  protected answerList: Answer[] = [];

  protected qaDictionary: { [qk: string]: string } = {};
  protected unansweredQuestions: string[] = [];

  public async created() {
    this.loadingPage = true;
    this.$watch(() => this.$route.params.id, this.loadJob);
    await this.loadJob(this.$route.params.id);

    await this.getAllQuestions();

    if (store.state.Answers.length === 0) {
      await this.getAllAnswers();
      await this.storeAllAnswers(this.answerList);
    }

    this.loadingPage = false;
  }

  public async loadJob(jobId: string) {
    this.loadingJob = true;

    try {
      await loadJobDetail(jobId);
      await loadExamDetail(jobId);
    } catch (err) {
      this.$router.push("/");
      this.$notify.error({
        title: "Error",
        message: err.message,
        offset: 48,
      });
    } finally {
      this.loadingJob = false;
    }
  }

  protected async storeAllAnswers(answers: Answer[]) {
    await store.commit("setAnswers", answers);
  }

  protected async doneClicked() {
    this.$router.push(`/Job/${store.state.Job.JobId}`);
  }

  public requiredPartsClicked() {
    this.$router.push(`/Job/${store.state.Job.JobId}/ExamDetail/Parts`);
  }

  protected async getAllAnswers() {
    this.loadingAnswer = true;
    this.answerList = [];

    const response = await axios.get(
      `${store.state.Environment.ODataBaseUrl}/ChecklistAnswers`,
      {
        headers: {
          Authorization: "Bearer " + store.state.User.AccessToken,
        },
      }
    );

    if (response.status === 200 && response.data) {
      const answers = response.data.value;
      answers.forEach((a: any) => {
        const answer: Answer = {
          AnswerKey: a.Answerkey,
          AnswerText: a.Answertext,
        };

        this.answerList.push(answer);
      });

      this.loadingAnswer = false;
    }
  }

  protected async getAllQuestions() {
    this.loadingQuestion = true;

    if (this.jobDetail) {
      const response = await axios.get(
        `${store.state.Environment.ODataBaseUrl}/ChecklistQuestions?$filter=Inventorytype eq '${this.jobDetail.InventoryTypeId}' and ProblemTypeId eq '${this.jobDetail.JobType}'&$expand=SubgroupType($select=Description)` +
          `&$orderby=Sequence asc`,
        {
          headers: {
            Authorization: "Bearer " + store.state.User.AccessToken,
          },
        }
      );

      if (response.status === 200 && response.data) {
        const questions = response.data.value;
        // Group the questions into subgroups
        const questionsObj: { [k: string]: Question[] } = questions.reduce(
          (p: any, n: any) => {
            // Create question object
            const question: Question = {
              QuestionId: n.Questionkey,
              InventoryType: n.Inventorytype,
              QuestionText: n.Questiontext,
              QuestionSubgroupTitle: n.SubgroupTypeCode
                ? n.SubgroupType.Description
                : null,
            };
            this.qaDictionary[question.QuestionId] = "";
            // If null
            if (n.SubgroupType !== null) {
              if (!p[n.SubgroupType.Description]) {
                p[n.SubgroupType.Description] = [question];
              } else {
                p[n.SubgroupType.Description].push(question);
              }
            } else {
              if (!p["Default"]) {
                p["Default"] = [question];
              } else {
                p["Default"].push(question);
              }
            }
            return p;
          },
          {}
        );
        this.questionList = questionsObj;
        this.loadingQuestion = false;
      }
    }
  }

  get preChecklistQuestions(): BigQuestion[] {
    if (this.examDetail) {
      return [
        {
          question: "Arrived on Site:",
          function: () => {},
          component: {
            name: "DateTimePicker",
            props: {
              onDateInput: this.arrivedOnSiteDateChanged,
              onTimeInput: this.arrivedOnSiteTimeChanged,
              onNowClicked: this.ArrivedNowClicked,
              datePickerProps: {
                value: this.examDetail.ArrivedOnSite,
                placeholder: "Arrived on site date",
              },
              timePickerProps: {
                value: this.arrivedOnSiteTime,
                placeholder: "Select a booked time",
              },
            },
          },
        },
      ];
    }
    return [];
  }

  get bigQuestions() {
    if (this.examDetail) {
      const bigQuestions: BigQuestion[] = [
        {
          question: "Details of work carried out:",
          value: this.examDetail!.DefectWork || "",
          function: this.workDoneChanged,
        },
        {
          question: "Next Visit/Job Failed notes:",
          value: this.examDetail!.VisitNotes || "",
          function: this.nextVisitNotesChanged,
        },
      ];
      return bigQuestions;
    } else {
      return [];
    }
  }

  public async workDoneChanged(event: FocusEvent) {
    if (event.target) {
      try {
        await store.dispatch(
          ActionNames.SetDefectWork,
          (event.target as HTMLTextAreaElement).value
        );
      } catch (err) {
        this.$notify.error({
          title: "Error",
          message: err.message,
          offset: 48,
        });
      }
    }
  }

  public async nextVisitNotesChanged(event: FocusEvent) {
    if (event.target) {
      try {
        await store.dispatch(
          ActionNames.SetVisitNotes,
          (event.target as HTMLTextAreaElement).value
        );
      } catch (err) {
        this.$notify.error({
          title: "Error",
          message: err.message,
          offset: 48,
        });
      }
    }
  }

  get examDetail() {
    return store.state.Job.ExamDetail;
  }
  get jobDetail() {
    return store.state.Job.JobDetail;
  }

  get detailDescription() {
    if (this.jobDetail) {
      return `(${store.state.Job.JobId}) ${
        this.jobDetail!.TypeDescription
      } details for ${this.jobDetail!.DivisionName}`;
    }
  }

  protected changeAnswer(q: string, a: string) {
    this.qaDictionary[q] = a;
  }

  get arrivedOnSiteTime() {
    let arrivedTime: string = "00:00";
    if (this.examDetail!.ArrivedOnSite) {
      arrivedTime = this.examDetail!.ArrivedOnSite!.isValid()
        ? this.examDetail!.ArrivedOnSite!.format("HH:mm")
        : "00:00";
    }
    return arrivedTime;
  }

  public arrivedOnSiteDateChanged(newDate: string) {
    const currentArrivedOnSite = this.examDetail!.ArrivedOnSite;
    const arrivedOnSite: moment.Moment = moment(newDate);
    arrivedOnSite.set({
      hour: currentArrivedOnSite ? currentArrivedOnSite.get("hour") : 0,
      minute: currentArrivedOnSite ? currentArrivedOnSite.get("minute") : 0,
    });
    this.updateArrivedOnSite(arrivedOnSite);
  }

  public arrivedOnSiteTimeChanged(newTime: string) {
    const arrivedOnSite: moment.Moment =
      moment(this.examDetail!.ArrivedOnSite) || moment();
    const timeParts = newTime.split(":");
    arrivedOnSite.set({
      hour: parseInt(timeParts[0], 10),
      minute: parseInt(timeParts[1], 10),
    });
    this.updateArrivedOnSite(arrivedOnSite);
  }

  public ArrivedNowClicked() {
    const arrivedOnSite: moment.Moment = moment();
    let minutes = arrivedOnSite.get("minute");
    minutes = minutes - (minutes % 15);
    arrivedOnSite.set({
      minute: minutes,
      seconds: 0,
    });
    this.updateArrivedOnSite(arrivedOnSite);
  }

  public async updateArrivedOnSite(newValue: moment.Moment) {
    try {
      await store.dispatch(ActionNames.SetArrivedOnSite, newValue);
    } catch (err) {
      this.$notify.error({
        title: "Error",
        message: err.message,
        offset: 48,
      });
    }
  }
}
